import React, { ReactElement } from 'react'
import styled from 'styled-components'
import {
  Switch,
  InputField,
  InputType,
  Buttonlink,
  Button,
  ButtonStyle,
} from '@one-tree/library'
import { HiOutlineX } from 'react-icons/hi'
import { IContact } from '../../types/API'

const Styles = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 10px;
`
const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  h3 {
    margin: 0 7px 0 0;
  }
  margin: 10px 0;
`
const EmailRow = styled.div`
  display: flex;
  align-items: end;
  column-gap: 5px;
  > div {
    width: 100%;
  }
`
const Cross = styled(HiOutlineX)`
  width: 20px;
  height: 20px;
  display: flex;
`
interface IContactProps {
  heading: string
  contact: Partial<IContact>
  changeContact: (changes: Partial<IContact>) => void
  copyLink: ReactElement
  showNotifications: boolean
  multi?: true
}
export default function Contact(props: IContactProps): ReactElement {
  const {
    heading,
    contact,
    copyLink,
    changeContact,
    showNotifications,
    multi,
  } = props
  const {
    title, firstName, lastName, jobTitle, phone, email, notifications,
  } = contact

  const handleChange = <T extends unknown>(
    value: T,
    key: keyof IContact,
  ): void => {
    changeContact({
      [key]: value,
    })
  }

  const emailArray = typeof email === 'string' ? [email] : email || []

  const handleEmailChange = (
    value: string,
    index: number,
    isMulti: boolean,
  ): void => {
    if (!isMulti) {
      handleChange([value], 'email')
      return
    }

    const newEmails = [...emailArray]
    newEmails[index] = value
    handleChange(newEmails, 'email')
  }

  const handleRemoveEmail = (index: number): void => {
    const newEmails = [...emailArray]
    newEmails.splice(index, 1)
    handleChange(newEmails, 'email')
  }

  const isMulti = emailArray.length > 1

  const renderEmail = emailArray.map((each, index) => {
    const key = `email-${index}`

    return (
      <EmailRow>
        <InputField
          key={key}
          label={isMulti ? `Email ${index + 1}` : 'Email'}
          type={InputType.Email}
          value={each}
          onChange={(value): void => handleEmailChange(value, index, isMulti)}
        />
        {isMulti && (
          <Button
            buttonStyle={ButtonStyle.Danger}
            onClick={(): void => handleRemoveEmail(index)}
          >
            <Cross />
          </Button>
        )}
      </EmailRow>
    )
  })

  return (
    <Styles>
      <HeaderRow>
        <h3>{heading}</h3>
        {copyLink}
      </HeaderRow>
      <InputField
        label="Title"
        value={title}
        onChange={(value): void => handleChange(value, 'title')}
      />
      <InputField
        label="First name"
        value={firstName}
        onChange={(value): void => handleChange(value, 'firstName')}
      />
      <InputField
        label="Last name"
        value={lastName}
        onChange={(value): void => handleChange(value, 'lastName')}
      />
      <InputField
        label="Job title"
        value={jobTitle}
        onChange={(value): void => handleChange(value, 'jobTitle')}
      />
      <InputField
        label="Phone number"
        type={InputType.Phone}
        value={phone}
        onChange={(value): void => handleChange(value, 'phone')}
      />
      {renderEmail}
      {multi && (
        <Buttonlink
          onClick={(): void => handleChange([...emailArray, ''], 'email')}
        >
          Add another email...
        </Buttonlink>
      )}
      {showNotifications && (
        <Switch
          label="receives sales notifications?"
          flipLabel={true}
          value={Boolean(email) && (notifications || false)}
          switchAction={(isOn): void => handleChange(isOn, 'notifications')}
          disabled={!email}
        />
      )}
    </Styles>
  )
}
